/**
 * Dashboard KPI stat tile. The row leads the page, so each tile is the whole
 * card — no nested Card/Statistic — laid out as label + identity chip on top,
 * the number below it, then one line of context.
 *
 * Color: the tile hue is IDENTITY (which entity this counts), carried by the
 * chip and a 3px rail, never by the number — value/label/note stay on the text
 * tokens so a tile never reads as a status. The lifecycle hues (live/draft) only
 * appear on the note dots, where they mean the same thing as in the donuts.
 * Slots come from the validated dashboard palette in charts.css; never a raw hue.
 */

import type { CSSProperties, ReactNode } from "react";
import { Icon, type IconName } from "@app/ui";

/** Palette slot for the tile's identity mark (see charts.css). */
export type StatTone = "indigo" | "violet" | "teal" | "coral";

export interface StatTileProps {
  label: string;
  /** Preformatted, so locale number formatting stays with the caller. */
  value: string;
  icon: IconName;
  tone: StatTone;
  note: ReactNode;
  /** Position in the row — staggers the mount animation. */
  index?: number;
}

export function StatTile({ label, value, icon, tone, note, index = 0 }: StatTileProps) {
  return (
    <div
      className={`dpp-tile dpp-tile--${tone}`}
      style={{ "--tile-delay": `${index * 70}ms` } as CSSProperties}
    >
      <div className="dpp-tile__head">
        <span className="dpp-tile__label">{label}</span>
        <span className="dpp-tile__chip" aria-hidden="true">
          <Icon name={icon} size={16} />
        </span>
      </div>
      <div className="dpp-tile__value">{value}</div>
      <div className="dpp-tile__note">{note}</div>
    </div>
  );
}

/** Lifecycle dot for a tile note — same hues as the donut legends. */
export function StatDot({ tone }: { tone: "live" | "draft" }) {
  return <span className={`dpp-tile__dot dpp-tile__dot--${tone}`} aria-hidden="true" />;
}
